/**
 * Apps Page Component
 * Page for quick access to Microsoft 365 applications
 */

import { makeStyles, shorthands, tokens, Text, Title3 } from '@fluentui/react-components';
import { RoundedCard } from '../components/common/RoundedCard';
import { useTabLayoutStyles } from '../components/common/hooks/useTabLayoutStyles';
import { useAuth } from '../contexts/AuthContext';
import { OutlookIcon } from '../components/icons/OutlookIcon';
import { TeamsIcon } from '../components/icons/TeamsIcon';
import { OneDriveIcon } from '../components/icons/OneDriveIcon';
import { WordIcon } from '../components/icons/WordIcon';
import { ExcelIcon } from '../components/icons/ExcelIcon';
import { PowerPointIcon } from '../components/icons/PowerPointIcon';
import { OneNoteIcon } from '../components/icons/OneNoteIcon';
import { SharePointIcon } from '../components/icons/SharePointIcon';
import type { ReactNode } from 'react';

interface AppInfo {
  name: string;
  url: string;
  icon: ReactNode;
  description: string;
}

/**
 * Extract tenant domain from User Principal Name (UPN).
 * UPN format: user@domain
 * Examples:
 *   john@contoso.onmicrosoft.com -> contoso.onmicrosoft.com
 *   jane@contoso.com -> contoso.com
 */
function getDomainFromUPN(upn: string): string {
  const parts = upn.split('@');
  return parts.length === 2 ? parts[1] : '';
}

/**
 * Extract tenant name from domain for SharePoint URL construction.
 * Examples:
 *   contoso.onmicrosoft.com -> contoso
 *   contoso.com -> contoso
 */
function getTenantNameFromDomain(domain: string): string {
  // Remove .onmicrosoft.com suffix if present
  const withoutSuffix = domain.replace('.onmicrosoft.com', '');
  // Take first part before any dot
  const tenantName = withoutSuffix.split('.')[0];
  return tenantName;
}

/**
 * Build app URLs, with OneDrive and SharePoint URLs based on authenticated user's tenant.
 */
function buildAppUrls(userPrincipalName?: string | null): AppInfo[] {
  let oneDriveUrl = 'https://onedrive.live.com'; // Default fallback
  let sharePointUrl = 'https://www.office.com/launch/sharepoint'; // Default fallback

  if (userPrincipalName) {
    const domain = getDomainFromUPN(userPrincipalName);
    if (domain) {
      const tenantName = getTenantNameFromDomain(domain);
      oneDriveUrl = `https://${tenantName}-my.sharepoint.com`;
      sharePointUrl = `https://${tenantName}.sharepoint.com`;
    }
  }

  return [
    {
      name: 'Outlook',
      url: 'https://outlook.office.com',
      icon: <OutlookIcon size={80} />,
      description: 'Access your email, calendar, and contacts',
    },
    {
      name: 'Teams',
      url: 'https://teams.microsoft.com',
      icon: <TeamsIcon size={80} />,
      description: 'Chat, meet, call, and collaborate',
    },
    {
      name: 'OneDrive',
      url: oneDriveUrl,
      icon: <OneDriveIcon size={80} />,
      description: 'Store and share your files',
    },
    {
      name: 'SharePoint',
      url: sharePointUrl,
      icon: <SharePointIcon size={80} />,
      description: 'Share and manage content, knowledge, and applications',
    },
    {
      name: 'Word',
      url: 'https://office.com/launch/word',
      icon: <WordIcon size={80} />,
      description: 'Create and edit documents',
    },
    {
      name: 'Excel',
      url: 'https://office.com/launch/excel',
      icon: <ExcelIcon size={80} />,
      description: 'Create and edit spreadsheets',
    },
    {
      name: 'PowerPoint',
      url: 'https://office.com/launch/powerpoint',
      icon: <PowerPointIcon size={80} />,
      description: 'Create and edit presentations',
    },
    {
      name: 'OneNote',
      url: 'https://office.com/launch/onenote',
      icon: <OneNoteIcon size={80} />,
      description: 'Organize your notes and ideas',
    },
  ];
}

const useStyles = makeStyles({
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 280px)',
    gap: tokens.spacingHorizontalXL,
    width: '100%',
    maxWidth: `calc(4 * 280px + 3 * ${tokens.spacingHorizontalXL} + 100px + ${tokens.spacingHorizontalM})`,
    marginTop: tokens.spacingVerticalXL,
    marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalM,
    boxSizing: 'border-box',
  },
  card: {
    height: '100%',
    cursor: 'pointer',
    transitionProperty: 'transform, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: tokens.shadow16,
    },
  },
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXL),
    gap: tokens.spacingVerticalL,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    textAlign: 'center',
  },
  appName: {
    lineHeight: tokens.lineHeightBase300,
  },
  appDescription: {
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
  },
});

export function AppsPage() {
  const layoutStyles = useTabLayoutStyles();
  const styles = useStyles();
  const { user } = useAuth();

  // Build app URLs with tenant-specific OneDrive URL from user's UPN
  const apps = buildAppUrls(user?.user_principal_name);

  return (
    <div className={layoutStyles.container}>
      <div className={layoutStyles.headerCard}>
        {/* Page Header */}
        <div className={layoutStyles.header}>
          <h1 className={layoutStyles.title}>Apps</h1>
          <Text style={{ marginTop: tokens.spacingVerticalM }}>Click any card to open the app in a new browser tab</Text>
        </div>
      </div>

      {/* App Cards */}
      <div className={styles.cardsContainer}>
        {apps.map((app) => (
          <RoundedCard key={app.name} className={styles.card}>
            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.cardLink}
            >
              <div className={styles.iconContainer}>
                {app.icon}
              </div>
              <div className={styles.textContainer}>
                <Title3 className={styles.appName}>{app.name}</Title3>
                <Text className={styles.appDescription}>{app.description}</Text>
              </div>
            </a>
          </RoundedCard>
        ))}
      </div>
    </div>
  );
}
